import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { _getFundsPublic, _getFundsView, _getUsersFollowedFunds } from '@/api/fund/queries';
import type { FundCarouselItem } from '@/api/fund-views/types';
import { Tabs, theme, Typography } from '@/design-system';
import { useIsMobile } from '@/global-hooks/use-is-mobile';
import { FundCarouselCard } from '@/pages/all-funds/modules/fund-carousel-card';
import { FundCarouselCardSkeleton } from '@/pages/all-funds/modules/fund-carousel-card/skeleton';
import { EmptyState } from '@/shared/design-system';

import { mapPublicFundToCarouselItem, mapReferencedFundToCarouselItem } from './adapters';
import { FollowFundButton } from './follow-fund-button';
import { FollowingList } from './following-list';
import { FundCarousel } from './fund-carousel';
import {
  CuratedGrid,
  CuratedTabs,
  OpportunitiesColumn,
  OpportunitiesRoot,
  OpportunitiesSection,
  SectionHeader,
} from './opportunities.styled';

const CURATED_TABS = {
  topLists: 'top-10-lists',
  comingSoon: 'coming-soon',
};

const CURATED_FUNDS_COUNT = 4;
const FOLLOWING_COUNT = 10;

const Opportunities = () => {
  const isMobile = useIsMobile();

  const { data: funds, isPending: isFundsPending } = useQuery(_getFundsPublic());
  const { data: fundsView, isPending: isFundsViewPending } = useQuery(_getFundsView());
  const { data: usersFollowedFunds, isPending: isFollowedFundsPending } =
    useQuery(_getUsersFollowedFunds());

  const filteredFunds = useMemo(() => {
    return funds?.funds?.filter((f) => f.investmentAvailabilityStatus);
  }, [funds]);

  const publicFundsById = useMemo(
    () => new Map((funds?.funds ?? []).map((f) => [f.id, f])),
    [funds?.funds],
  );

  const followedFundIds = useMemo(
    () => new Set((usersFollowedFunds?.followedFunds ?? []).map((f) => f.fundId)),
    [usersFollowedFunds?.followedFunds],
  );

  const allFunds = useMemo(
    () => filteredFunds?.map(mapPublicFundToCarouselItem) ?? [],
    [filteredFunds],
  );

  const followedFunds = useMemo(
    () =>
      (usersFollowedFunds?.followedFunds ?? []).map((f) =>
        mapReferencedFundToCarouselItem(f, publicFundsById.get(f.fundId)),
      ),
    [publicFundsById, usersFollowedFunds?.followedFunds],
  );

  const recentlyWatchedFunds = useMemo(
    () =>
      (fundsView?.fundViews ?? []).map((f) =>
        mapReferencedFundToCarouselItem(f, publicFundsById.get(f.fundId)),
      ),
    [fundsView?.fundViews, publicFundsById],
  );

  const curatedFunds = allFunds.slice(0, CURATED_FUNDS_COUNT);

  // Both join against publicFundsById, which renders null AUM and returns until it lands.
  const isRecentlyWatchedPending = isFundsPending || isFundsViewPending;
  const isFollowingPending = isFundsPending || isFollowedFundsPending;

  const renderFollowAction = (fund: FundCarouselItem) => (
    <FollowFundButton
      fundId={fund.fundId}
      fundName={fund.fundName}
      isFollowed={followedFundIds.has(fund.fundId)}
      fullWidth
    />
  );

  return (
    <OpportunitiesRoot>
      <OpportunitiesColumn>
        <FundCarousel
          title="Recommendations"
          items={allFunds}
          visibleCount={2}
          emptyMessage="No fund recommendations yet."
          isLoading={isFundsPending}
          renderAction={renderFollowAction}
        />

        <FundCarousel
          title="Recently Watched"
          items={recentlyWatchedFunds}
          visibleCount={2}
          emptyMessage="No recently watched funds yet."
          isLoading={isRecentlyWatchedPending}
          renderAction={renderFollowAction}
        />

        <OpportunitiesSection>
          <SectionHeader>
            <Typography as="h2" variant="h5" color={theme.colors.text.primary}>
              Curated Funds
            </Typography>

            {isMobile && !isFundsPending && curatedFunds.length > 0 && (
              <Typography variant="body3" color={theme.colors.text.secondary}>
                {`${curatedFunds.length} funds`}
              </Typography>
            )}
          </SectionHeader>

          <CuratedTabs defaultValue={CURATED_TABS.topLists} variant="bordered" size="md">
            <Tabs.List>
              <Tabs.Tab value={CURATED_TABS.topLists} label="Top 10 Lists" />
              <Tabs.Tab value={CURATED_TABS.comingSoon} label="Coming Soon" disabled />
            </Tabs.List>

            <Tabs.Panel value={CURATED_TABS.topLists}>
              {isFundsPending && (
                <CuratedGrid role="status" aria-label="Loading curated funds">
                  {Array.from({ length: CURATED_FUNDS_COUNT }, (_, index) => (
                    <FundCarouselCardSkeleton key={index} />
                  ))}
                </CuratedGrid>
              )}

              {!isFundsPending && curatedFunds.length === 0 && (
                <EmptyState variant="tertiary" title="No curated funds yet." />
              )}

              {!isFundsPending && curatedFunds.length > 0 && (
                <CuratedGrid>
                  {curatedFunds.map((fund) => (
                    <FundCarouselCard
                      key={fund.fundId}
                      data={fund}
                      action={renderFollowAction(fund)}
                    />
                  ))}
                </CuratedGrid>
              )}
            </Tabs.Panel>
          </CuratedTabs>
        </OpportunitiesSection>
      </OpportunitiesColumn>

      <OpportunitiesSection>
        <Typography as="h2" variant="h5" color={theme.colors.text.primary}>
          Following
        </Typography>

        <FollowingList
          funds={followedFunds.slice(0, FOLLOWING_COUNT)}
          isLoading={isFollowingPending}
        />
      </OpportunitiesSection>
    </OpportunitiesRoot>
  );
};

export default Opportunities;
